import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { samplingPlansAPI, reportsAPI } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import ExpandableTree from '../components/ExpandableTree';

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const STAGE_LABELS = {
  draft: 'Draft — awaiting cleaning sign-off',
  cleaned: 'Cleaned — awaiting sampling sign-off',
  sampled: 'Sampled — awaiting visual inspection',
  visual_done: 'Visual passed — awaiting analysis',
  analyzed: 'Analyzed — awaiting report/review',
  reported: 'Reported',
  approved: 'Approved'
};

// Every equipment/location node in the tree is expanded by default, so opening a study
// report shows the full picture (every sampled location) rather than requiring the user
// to click through each branch — matches "reporting also required for all sampled locations".
const collectDefaultExpandedIds = (tree) => {
  const ids = [];
  const walk = (nodes) => {
    nodes.forEach((node) => {
      if (Array.isArray(node.children) && node.children.length) {
        ids.push(node.id);
        walk(node.children);
      }
    });
  };
  walk(tree || []);
  return ids;
};

const CVReport = () => {
  const { planId: routePlanId } = useParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(routePlanId || '');
  const [report, setReport] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoadingPlans(true);
        const res = await samplingPlansAPI.getAll();
        const rows = getRows(res);
        setPlans(rows);
        if (!routePlanId && rows.length) setSelectedPlanId(String(rows[0].plan_id));
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load sampling plans');
      } finally {
        setLoadingPlans(false);
      }
    };
    loadPlans();
  }, [routePlanId]);

  useEffect(() => {
    if (!selectedPlanId) { setReport(null); return; }
    const loadReport = async () => {
      try {
        setLoadingReport(true);
        setSelectedNode(null);
        const res = await reportsAPI.getStudy(selectedPlanId);
        setReport(res?.data?.data || null);
      } catch (error) {
        setReport(null);
        toast.error(error?.response?.data?.message || 'Failed to load study report');
      } finally {
        setLoadingReport(false);
      }
    };
    loadReport();
  }, [selectedPlanId]);

  const defaultExpandedIds = useMemo(() => collectDefaultExpandedIds(report?.tree), [report]);

  const handlePlanChange = (value) => {
    setSelectedPlanId(value);
    navigate(value ? `/study-report/${value}` : '/study-report', { replace: true });
  };

  const isSampleTypeNode = (node) => node && !Array.isArray(node.children);

  const plan = report?.plan;

  return (
    <div>
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header">
          <h2 className="card-title">Study Report</h2>
        </div>
        <div style={{ padding: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ minWidth: '320px' }}>
            <label className="form-label">Sampling Plan</label>
            <select className="form-control" value={selectedPlanId} disabled={loadingPlans}
              onChange={(e) => handlePlanChange(e.target.value)}>
              <option value="">Select a plan…</option>
              {plans.map((p) => (
                <option key={p.plan_id} value={p.plan_id}>
                  {p.plan_code} — {p.study_type} {p.train_name ? `(${p.train_name})` : ''}
                </option>
              ))}
            </select>
          </div>
          {plan && (
            <>
              <div><strong>Study Type:</strong> {plan.study_type}</div>
              <div>
                <strong>Train:</strong>{' '}
                {plan.train_id ? (
                  <span className="record-link" onClick={() => navigate('/equipment-trains', { state: { openViewTrainId: plan.train_id } })} title="View train">
                    {plan.train_code} {plan.train_name}
                  </span>
                ) : `${plan.train_code || ''} ${plan.train_name || ''}`}
              </div>
              <div><strong>Status:</strong> <StatusBadge status={plan.status} /></div>
            </>
          )}
        </div>
      </div>

      {loadingReport && <div className="card" style={{ padding: '24px', textAlign: 'center' }}>Loading report…</div>}

      {!loadingReport && report && (
        <div className="card">
          <div className="card-header"><h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Sampled Locations</h2></div>
          <div style={{ display: 'flex', gap: '16px', padding: '16px' }}>
            <div style={{ flex: '0 0 380px', maxWidth: '380px', borderRight: '1px solid var(--border-color)', paddingRight: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
              <ExpandableTree
                nodes={report.tree}
                selectedId={selectedNode?.id}
                defaultExpandedIds={defaultExpandedIds}
                onSelect={setSelectedNode}
              />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {!selectedNode && (
                <div style={{ color: 'var(--text-secondary)', padding: '12px' }}>
                  Select an equipment, location, or sample type on the left to see its results.
                </div>
              )}

              {selectedNode && isSampleTypeNode(selectedNode) && (
                <SampleTypeDetail node={selectedNode} />
              )}

              {selectedNode && !isSampleTypeNode(selectedNode) && (
                <div style={{ color: 'var(--text-secondary)', padding: '12px' }}>
                  {selectedNode.children?.length || 0} item(s) under "{selectedNode.label}" — expand to a sample type to see its results.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loadingReport && selectedPlanId && !report && (
        <div className="card" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          No report data available for this plan.
        </div>
      )}
    </div>
  );
};

const SampleTypeDetail = ({ node }) => {
  const { location, criteria, results } = node;
  return (
    <div>
      <h3 style={{ fontSize: 'var(--fs-lg)', marginBottom: '4px' }}>{location.location_name} — {location.sample_type}</h3>
      {location.rationale && <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '10px' }}>{location.rationale}</div>}

      <div style={{ marginBottom: '14px' }}>
        <strong style={{ fontSize: 'var(--fs-md)' }}>Acceptance Criteria</strong>
        <div className="table-container" style={{ marginTop: '6px' }}>
          <table>
            <thead>
              <tr><th>Criterion</th><th>Limit</th><th>Unit</th></tr>
            </thead>
            <tbody>
              {criteria.length === 0
                ? <tr><td colSpan={3} className="text-center text-muted">No criteria recorded</td></tr>
                : criteria.map((c) => (
                    <tr key={c.limit_id}>
                      <td>{c.criterion_label || c.limit_type}</td>
                      <td>{c.limit_value ?? '—'}</td>
                      <td>{c.limit_unit || ''}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <strong style={{ fontSize: 'var(--fs-md)' }}>Results</strong>
        <div className="table-container" style={{ marginTop: '6px' }}>
          <table>
            <thead>
              <tr><th>Batch</th><th>Result</th><th>Limit</th><th>Status</th><th>Stage</th><th>Entered By</th></tr>
            </thead>
            <tbody>
              {results.length === 0
                ? <tr><td colSpan={6} className="text-center text-muted">No results entered yet</td></tr>
                : results.map((r) => (
                    <tr key={r.result_id}>
                      <td>{r.batch_number || '—'}</td>
                      <td>{r.result_value ?? '—'}</td>
                      <td>{r.limit_value ?? '—'}</td>
                      <td>
                        {r.result_status ? (
                          <span style={{
                            fontWeight: 700, fontSize: 'var(--fs-sm)',
                            color: r.result_status === 'FAIL' ? 'var(--danger-color)' : r.result_status === 'PASS' ? 'var(--success-color)' : 'var(--text-secondary)'
                          }}>{r.result_status}</span>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: 'var(--fs-sm)' }}>{STAGE_LABELS[r.workflow_stage] || r.workflow_stage || '—'}</td>
                      <td>{r.entered_by_name || '—'}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CVReport;
